import * as amplitude from '@amplitude/analytics-browser'
import { AppBar, AppBarProps, Box, Button, Chip, IconButton, Toolbar } from '@mui/material'
import Stack from '@mui/material/Stack'
import { styled, useTheme } from '@mui/material/styles'
import Cookies from 'js-cookie'
import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { config as appConfig } from '../../../core'
import { getUserDisplayName } from '@/features/auth'
import { useSignOut } from '../../../hooks'
import { AvatarIcon, Logout, MenuFold, MenuOpen } from '../../../icons'
import { RootState, useTypedDispatch, useTypedSelector } from '../../../store'
import { LanguageSelector } from './Components/LanguageSelector/LanguageSelector'
import {
  navbarRootStyles,
  toolbarStyles,
  menuToggleButtonStyles,
  spacerStyles,
  iconPlaceholderStyles,
  userButtonStyles,
  avatarIconStyles,
  userChipStyles,
  logoutButtonStyles,
} from './AppToolBarStyles'
import CustomPalette from '@/theme/CustomPalette'

const drawerWidth = 240

type AppToolBarProps = AppBarProps & {
  isOpen?: boolean
  onDrawerToggle: () => void
}

const NavbarRoot = styled(AppBar)(() => ({
  backgroundColor: CustomPalette.SecondaryCloud,
  boxShadow: 'none',
}))

export const AppToolBar = (props: AppToolBarProps): JSX.Element => {
  const { t } = useTranslation('general')
  const { isOpen, onDrawerToggle, ...other } = props
  const menuAnchorRef = React.createRef<HTMLButtonElement>()
  const theme = useTheme()
  const user = useTypedSelector((state: RootState) => state.authSlice.user)
  const dispatch = useTypedDispatch()
  const [selectedTenantLanguage, setSelectedTenantLanguage] = useState('')
  const signOutAction = useSignOut()

  const IconPlaceholder = () => <Box sx={iconPlaceholderStyles} />

  const isAccessTokenValid = () => {
    const expiryTime = Cookies.get('access_token_expiry')
    if (!expiryTime) return false

    const currentTime = new Date().getTime()
    return currentTime < Number(expiryTime)
  }

  function signOut(event: React.MouseEvent): void {
    if (!isAccessTokenValid()) {
      event.preventDefault()
      amplitude.reset()
      dispatch(signOutAction)
      window.location.href = `${appConfig.api.origin}/logout?redirect_url=${encodeURIComponent(
        appConfig.api.redirect_url,
      )}`
    }
  }

  return (
    <NavbarRoot
      position="fixed"
      sx={navbarRootStyles(theme, isOpen ?? true, drawerWidth)}
      color="default"
      elevation={1}
      {...other}>
      <Toolbar disableGutters sx={toolbarStyles}>
        <IconButton color="inherit" edge="start" sx={menuToggleButtonStyles} onClick={onDrawerToggle}>
          {isOpen ? <MenuFold /> : <MenuOpen />}
        </IconButton>

        <Box sx={spacerStyles} />

        {user && (
          <Stack direction="row">
            <LanguageSelector
              selectedLanguage={selectedTenantLanguage}
              setSelectedLanguage={setSelectedTenantLanguage}
            />
            <Button endIcon={<IconPlaceholder />} size="small" ref={menuAnchorRef} sx={userButtonStyles}>
              <Chip icon={<AvatarIcon sx={avatarIconStyles} />} label={getUserDisplayName(user)} sx={userChipStyles} />
            </Button>

            <Button onClick={signOut} startIcon={<Logout />} sx={logoutButtonStyles}>
              {t('logout')}
            </Button>
          </Stack>
        )}
      </Toolbar>
    </NavbarRoot>
  )
}
